(() => {
  const scope = typeof window !== 'undefined' ? window : globalThis;

  function pickNextSoundIndex(poolLength, previousIndex, random = Math.random) {
    if (poolLength <= 0) return -1;
    if (poolLength === 1) return previousIndex === 0 ? -1 : 0;

    const candidate = Math.floor(random() * (poolLength - 1));
    return candidate >= previousIndex && previousIndex >= 0 ? candidate + 1 : candidate;
  }

  function pickRandomPatternIndex(patternCount, random = Math.random) {
    if (patternCount <= 0) return -1;
    return Math.min(patternCount - 1, Math.floor(random() * patternCount));
  }

  function fillTemplate(template, values) {
    return Object.entries(values).reduce(
      (result, [key, value]) => result
        .replaceAll(`__${key.toUpperCase()}__`, String(value))
        .replaceAll(`{${key}}`, String(value)),
      template,
    );
  }

  scope.BabaubaPatternWorldUtils = { pickNextSoundIndex, pickRandomPatternIndex, fillTemplate };
  if (typeof window === 'undefined' || typeof document === 'undefined' || !window.customElements) return;

  if (!customElements.get('kids-pattern-world')) {
    customElements.define(
      'kids-pattern-world',
      class KidsPatternWorld extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.carouselElement = this.querySelector('[data-pattern-carousel]');
          this.wrapper = this.carouselElement?.querySelector('.swiper-wrapper');
          if (!this.carouselElement || !this.wrapper) return;

          this.previousButton = this.querySelector('[data-action="previous-pattern"]');
          this.nextButton = this.querySelector('[data-action="next-pattern"]');
          this.progressElement = this.querySelector('[data-pattern-progress]');
          this.questionState = new Map();
          this.patternData = new Map();
          this.lastSoundByPattern = new Map();
          this.currentPatternHandle = '';
          this.fallbackIndex = 0;
          this.audioRequestId = 0;
          this.audio = new Audio();
          this.audio.preload = 'none';
          this.audio.addEventListener('ended', () => this.onAudioEnded());

          this.realSlides = [...this.wrapper.querySelectorAll(':scope > .kpw__slide')];
          const language = document.documentElement.lang || 'de';
          const collator = new Intl.Collator(language, { sensitivity: 'base', numeric: true });
          this.realSlides
            .sort((first, second) => collator.compare(first.dataset.patternTitle || '', second.dataset.patternTitle || ''))
            .forEach((slide) => this.wrapper.appendChild(slide));

          this.realSlides.forEach((slide) => {
            const handle = slide.dataset.patternHandle;
            this.patternData.set(handle, {
              cards: this.parseJSON(slide.querySelector('[data-pattern-cards]'), []),
              sounds: this.parseJSON(slide.querySelector('[data-pattern-sounds]'), []).filter((sound) => sound?.url),
            });
            this.questionState.set(handle, { index: 0, expanded: false });
          });

          if (!this.realSlides.length) {
            this.previousButton?.setAttribute('hidden', '');
            this.nextButton?.setAttribute('hidden', '');
            return;
          }

          this.addEventListener('click', (event) => this.onClick(event));
          this.initCarousel();
        }

        disconnectedCallback() {
          this.stopAudio();
          this.swiper?.destroy?.(true, true);
        }

        parseJSON(element, fallback) {
          try {
            return JSON.parse(element?.textContent || '') || fallback;
          } catch (error) {
            console.error('Musterwelt: Ungültige eingebettete Daten.', error);
            return fallback;
          }
        }

        initCarousel() {
          const requestedHandle = new URL(window.location.href).searchParams.get('motiv');
          const requestedIndex = requestedHandle
            ? this.realSlides.findIndex((slide) => slide.dataset.patternHandle === requestedHandle)
            : -1;
          const initialIndex = requestedIndex >= 0
            ? requestedIndex
            : pickRandomPatternIndex(this.realSlides.length);
          const canLoop = this.realSlides.length > 1;

          if (window.FoxTheme?.Carousel) {
            this.carousel = new window.FoxTheme.Carousel(this.carouselElement, {
              slidesPerView: 1,
              spaceBetween: 20,
              loop: canLoop,
              speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 450,
              threshold: 5,
              grabCursor: canLoop,
              navigation: canLoop ? { nextEl: this.nextButton, prevEl: this.previousButton } : false,
              keyboard: { enabled: true, onlyInViewport: true },
              a11y: { enabled: true },
            });
            this.carousel.init();
            this.swiper = this.carousel.slider;
            this.swiper.on('realIndexChange', () => this.activatePattern(this.swiper.realIndex));
            this.swiper.on('transitionEnd', () => this.updateSlideAccessibility(this.currentPatternHandle, true));
            if (initialIndex) this.swiper.slideToLoop(initialIndex, 0, false);
          } else {
            this.classList.add('kpw--fallback');
            this.fallbackIndex = initialIndex;
          }

          this.classList.add('kpw--ready');
          this.previousButton?.toggleAttribute('hidden', !canLoop);
          this.nextButton?.toggleAttribute('hidden', !canLoop);
          this.activatePattern(initialIndex, true);
        }

        onClick(event) {
          const control = event.target.closest('[data-action]');
          if (!control || !this.contains(control)) return;
          const action = control.dataset.action;
          const slide = control.closest('.kpw__slide');
          const handle = slide?.dataset.patternHandle || this.currentPatternHandle;

          if (action === 'previous-pattern' && !this.swiper) {
            this.fallbackIndex = (this.fallbackIndex - 1 + this.realSlides.length) % this.realSlides.length;
            this.activatePattern(this.fallbackIndex);
          } else if (action === 'next-pattern' && !this.swiper) {
            this.fallbackIndex = (this.fallbackIndex + 1) % this.realSlides.length;
            this.activatePattern(this.fallbackIndex);
          } else if (action === 'show-answer') {
            const state = this.questionState.get(handle);
            if (!state) return;
            state.expanded = true;
            this.renderQuestion(handle);
          } else if (action === 'next-question') {
            const data = this.patternData.get(handle);
            const state = this.questionState.get(handle);
            if (!state || !data?.cards.length) return;
            state.index = (state.index + 1) % data.cards.length;
            state.expanded = false;
            this.renderQuestion(handle);
          } else if (action === 'play-sound') {
            this.playRandomSound(handle);
          }
        }

        activatePattern(index, initial = false) {
          const normalizedIndex = ((index % this.realSlides.length) + this.realSlides.length) % this.realSlides.length;
          const nextSlide = this.realSlides[normalizedIndex];
          const nextHandle = nextSlide.dataset.patternHandle;

          if (this.currentPatternHandle && this.currentPatternHandle !== nextHandle) {
            const previousState = this.questionState.get(this.currentPatternHandle);
            if (previousState) previousState.expanded = false;
            this.renderQuestion(this.currentPatternHandle);
          }
          this.stopAudio();
          this.currentPatternHandle = nextHandle;
          this.fallbackIndex = normalizedIndex;
          this.renderQuestion(nextHandle);
          this.updateSlideAccessibility(nextHandle);
          this.updateProgress(normalizedIndex);
          this.updateURL(nextHandle, initial);
        }

        renderQuestion(handle) {
          const data = this.patternData.get(handle);
          const state = this.questionState.get(handle);
          if (!data || !state) return;
          const card = data.cards[state.index];
          const matchingSlides = this.querySelectorAll(`.kpw__slide[data-pattern-handle="${CSS.escape(handle)}"]`);

          matchingSlides.forEach((slide) => {
            const questionUI = slide.querySelector('[data-question-ui]');
            const emptyUI = slide.querySelector('[data-no-questions]');
            const soundButton = slide.querySelector('[data-action="play-sound"]');
            const singleSoundPlayed = data.sounds.length === 1 && this.lastSoundByPattern.get(handle) === 0;

            questionUI?.toggleAttribute('hidden', !card);
            emptyUI?.toggleAttribute('hidden', Boolean(card));
            if (card) {
              const question = slide.querySelector('[data-question]');
              const answer = slide.querySelector('[data-answer]');
              const answerWrap = slide.querySelector('[data-answer-wrap]');
              const showAnswer = slide.querySelector('[data-action="show-answer"]');
              const nextQuestion = slide.querySelector('[data-action="next-question"]');
              const questionProgress = slide.querySelector('[data-question-progress]');
              if (question) question.textContent = card.question;
              if (answer) answer.textContent = card.answer;
              if (questionProgress) {
                questionProgress.textContent = fillTemplate(this.dataset.questionProgress, {
                  current: state.index + 1,
                  total: data.cards.length,
                });
              }
              answerWrap?.toggleAttribute('hidden', !state.expanded);
              showAnswer?.toggleAttribute('hidden', state.expanded);
              nextQuestion?.toggleAttribute('hidden', !state.expanded);
            }
            soundButton?.toggleAttribute('hidden', data.sounds.length === 0);
            if (soundButton) {
              soundButton.disabled = singleSoundPlayed;
              soundButton.setAttribute(
                'aria-label',
                singleSoundPlayed ? this.dataset.singleSoundFinished : this.dataset.playSound,
              );
            }
          });
        }

        updateSlideAccessibility(activeHandle, preferActiveClass = false) {
          const slides = this.querySelectorAll('.kpw__slide');
          slides.forEach((slide) => {
            const matchesHandle = slide.dataset.patternHandle === activeHandle;
            const isActive = matchesHandle && (!this.swiper || !preferActiveClass || slide.classList.contains('swiper-slide-active'));
            slide.setAttribute('aria-hidden', String(!isActive));
            if ('inert' in slide) slide.inert = !isActive;
            if (!this.swiper) slide.toggleAttribute('hidden', !isActive);
          });
        }

        updateProgress(index) {
          if (!this.progressElement) return;
          this.progressElement.textContent = fillTemplate(this.dataset.patternProgress, {
            current: index + 1,
            total: this.realSlides.length,
          });
        }

        updateURL(handle) {
          const url = new URL(window.location.href);
          url.searchParams.set('motiv', handle);
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        }

        stopAudio() {
          if (!this.audio) return;
          this.audioRequestId += 1;
          this.audio.pause();
          this.audio.removeAttribute('src');
          this.audio.load();
          this.playingHandle = '';
          this.setAudioStatus('', '');
        }

        async playRandomSound(handle) {
          const data = this.patternData.get(handle);
          if (!data?.sounds.length) return;
          const previousIndex = this.lastSoundByPattern.get(handle);
          const nextIndex = pickNextSoundIndex(data.sounds.length, previousIndex);
          if (nextIndex < 0) return;

          this.stopAudio();
          const requestId = this.audioRequestId;
          this.playingHandle = handle;
          this.lastSoundByPattern.set(handle, nextIndex);
          this.renderQuestion(handle);
          this.audio.src = data.sounds[nextIndex].url;
          this.setAudioStatus(handle, this.dataset.soundPlaying);
          try {
            await this.audio.play();
            if (requestId !== this.audioRequestId) return;
            this.renderQuestion(handle);
          } catch (error) {
            if (requestId !== this.audioRequestId) return;
            this.playingHandle = '';
            this.audio.removeAttribute('src');
            if (previousIndex === undefined) this.lastSoundByPattern.delete(handle);
            else this.lastSoundByPattern.set(handle, previousIndex);
            this.renderQuestion(handle);
            this.setAudioStatus(handle, this.dataset.soundError);
          }
        }

        onAudioEnded() {
          const handle = this.playingHandle;
          this.playingHandle = '';
          this.setAudioStatus(handle, '');
          if (handle) this.renderQuestion(handle);
        }

        setAudioStatus(handle, message) {
          this.querySelectorAll('[data-audio-status]').forEach((element) => {
            const slideHandle = element.closest('.kpw__slide')?.dataset.patternHandle;
            element.textContent = slideHandle === handle ? message : '';
          });
        }
      },
    );
  }
})();
